import React, { Component, useRef, useEffect, useState, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useVisitorTracking } from '../hooks/useVisitorTracking';
import { logDebug } from '../utils/logger';
import { getLikeStatus, toggleLike, getBatchLikeCounts } from '../utils/likesCache';
import { API_CONFIG } from '../config/apiConfig';
import { PORTFOLIO_ITEMS } from '../data/portfolioData';

/**
 * ============================================================================
 * ERROR BOUNDARY COMPONENT
 * ============================================================================
 * @class CarouselErrorBoundary
 * @description Catches JavaScript/render exceptions in the 3D Carousel subtree
 * and displays a developer-friendly fallback UI message detailing any failures.
 */
class CarouselErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log exception details for developer debugging
    console.error('🚨 3D Carousel Error Boundary Caught an Error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="carousel-fallback-container" style={{
          padding: '40px 20px',
          margin: '30px auto',
          maxWidth: '600px',
          background: 'rgba(25, 5, 25, 0.9)',
          border: '2px dashed rgba(255, 45, 85, 0.6)',
          borderRadius: '20px',
          textAlign: 'center',
          color: '#ffffff',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)'
        }}>
          <div className="carousel-fallback-card">
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#FF2D55', marginBottom: '15px' }}></i>
            <h3 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '10px', color: '#FF2D55' }}>
              3D Carousel Component Error
            </h3>
            <p style={{ fontSize: '14px', lineHeight: '1.6', color: 'rgba(255, 255, 255, 0.85)', marginBottom: '15px' }}>
              {this.state.error?.message || 'An error occurred while rendering or updating the 3D carousel.'}
            </p>
            <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic', marginBottom: '20px' }}>
              Developer Note: Ensure responsiveness, touch event listeners, and portfolio items data format are correct.
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
              style={{
                padding: '10px 24px',
                background: 'linear-gradient(135deg, #FF2D55 0%, #4B0082 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '25px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'transform 0.2s ease'
              }}
            >
              <i className="fas fa-redo" style={{ marginRight: '8px' }}></i> Retry Component
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * ============================================================================
 * PORTFOLIO DATASET
 * ============================================================================
 * Static array of portfolio projects rendered inside the 3D curved track.
 * Imported from centralized configuration.
 */

/**
 * ============================================================================
 * MAIN PORTFOLIO COMPONENT
 * ============================================================================
 * @component Portfolio
 * @description Main component rendering the 3D perspective carousel with smooth drag,
 * double-click to like, touch swipe support, and failure fallback error boundary.
 */
const PortfolioInner = memo(() => {
  // Visitor analytics tracking hook
  const { ref: portfolioRef } = useVisitorTracking('portfolio', {
    threshold: 0.3,
    minDuration: 4
  });

  // State for notification popups
  const [showNotification, setShowNotification] = useState(false);
  const [showLikeNotification, setShowLikeNotification] = useState(false);
  const [likeNotificationMessage, setLikeNotificationMessage] = useState('');

  // State for button pulse animation
  const [isCarouselAnimating, setIsCarouselAnimating] = useState(false);

  /**
   * Triggers header button click animation feedback
   */
  const handleDiscoverClick = () => {
    logDebug('Discover more clicked');
    if (isCarouselAnimating) return;

    setIsCarouselAnimating(true);
    setTimeout(() => {
      setIsCarouselAnimating(false);
    }, 1000);
  };

  // Likes state map: { [projectId]: { liked: boolean, count: number } }
  const [likes, setLikes] = useState({});

  /**
   * Sends interaction analytics telemetry to backend tracking service
   * @param {Object} item - Portfolio item payload
   * @param {string} interactionType - e.g. 'click', 'hover', 'like'
   * @param {number} originalIndex - Index in dataset
   */
  const trackProjectInteraction = useCallback(async (item, interactionType = 'click', originalIndex = 0) => {
    const TRACK_URL = API_CONFIG?.ENDPOINTS?.TRACK_VISIT;
    if (!TRACK_URL) return;

    const projectId = item.id || `portfolio-${originalIndex % PORTFOLIO_ITEMS.length}-${item.title.replace(/\s+/g, '-').toLowerCase()}`;
    const isAvailable = item.href !== "#";

    try {
      await fetch(TRACK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          section: `portfolio-item-${interactionType}`,
          duration: 1,
          sessionId: `${Date.now()}-${Math.random()}`,
          pageUrl: window.location.href,
          projectTitle: item.title,
          projectType: 'portfolio-carousel',
          projectId: projectId,
          projectAvailable: isAvailable,
          projectUrl: isAvailable ? item.href : '',
          interactionType: interactionType
        }),
      });

      logDebug(`📊 Portfolio Project Interaction: ${item.title} - ${interactionType}`, {
        projectId,
        available: isAvailable,
        url: item.href
      });
    } catch (error) {
      console.warn('Portfolio project interaction tracking failed:', error);
    }
  }, []);

  // DOM Refs for carousel tracking and momentum
  const carouselRef = useRef(null);
  const trackRef = useRef(null);

  // Dragging and hover state
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Velocity tracking for smooth momentum drag left/right
  const lastXRef = useRef(0);
  const velocityRef = useRef(0);
  const momentumFrameRef = useRef(null);

  // Touch event tracking
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [hasMoved, setHasMoved] = useState(false);
  const touchTimeoutRef = useRef(null);

  // Double-click & heart animation state
  const [clickCount, setClickCount] = useState({});
  const [showHeartAnimation, setShowHeartAnimation] = useState({});
  const clickTimeoutRef = useRef({});

  // Hover tracking throttle set
  const hoveredItemsRef = useRef(new Set());

  // Ref storing volatile state for the requestAnimationFrame loop
  const stateRef = useRef({
    isHovered: false,
    isDragging: false
  });

  useEffect(() => {
    stateRef.current = {
      isHovered,
      isDragging
    };
  }, [isHovered, isDragging]);

  /**
   * Generates and shows random encouraging like/unlike toast notifications
   */
  const showLikeNotificationMessage = useCallback((isLiked, projectTitle) => {
    const likeMessages = [
      `Thank you for making our ${projectTitle} project butter! 🧈✨`,
      `Your love for ${projectTitle} makes our hearts flutter! 💖`,
      `Thanks for spreading the love on ${projectTitle}! 🌟`,
      `You just made ${projectTitle} even more awesome! 🚀`,
      `Your appreciation for ${projectTitle} means the world to us! 🌍`,
      `Thanks for showing ${projectTitle} some love! 💝`
    ];

    const unlikeMessages = [
      `We'll keep working to make ${projectTitle} even better! 💪`,
      `Thanks for your honest feedback on ${projectTitle}! 🙏`,
      `We appreciate your interaction with ${projectTitle}! ✨`,
      `Your opinion helps us improve ${projectTitle}! 📈`,
      `Thanks for taking the time to engage with ${projectTitle}! 🤝`
    ];

    const messages = isLiked ? likeMessages : unlikeMessages;
    const randomMessage = messages[Math.floor(Math.random() * messages.length)];

    setLikeNotificationMessage(randomMessage);
    setShowLikeNotification(true);

    setTimeout(() => {
      setShowLikeNotification(false);
    }, 3000);
  }, []);

  /**
   * Handles hover tracking (throttled once per item per session)
   */
  const handleItemHover = useCallback((item, originalIndex) => {
    if (hoveredItemsRef.current.has(item.id)) return;
    hoveredItemsRef.current.add(item.id);
    trackProjectInteraction(item, 'hover', originalIndex);
  }, [trackProjectInteraction]);

  /**
   * Handles click & double-click interactions (Single-click = open project, Double-click = like project)
   */
  const handleItemClick = useCallback(async (e, item, originalIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const itemId = item.id;
    const currentCount = clickCount[itemId] || 0;
    const newCount = currentCount + 1;

    if (clickTimeoutRef.current[itemId]) {
      clearTimeout(clickTimeoutRef.current[itemId]);
    }

    setClickCount(prev => ({ ...prev, [itemId]: newCount }));

    if (newCount === 1) {
      clickTimeoutRef.current[itemId] = setTimeout(() => {
        if (item.href === "#") {
          trackProjectInteraction(item, 'unavailable-click', originalIndex);
          setShowNotification(true);
        } else {
          trackProjectInteraction(item, 'click', originalIndex);
          window.open(item.href, '_blank', 'noopener,noreferrer');
        }
        setClickCount(prev => ({ ...prev, [itemId]: 0 }));
      }, 300);
    } else if (newCount === 2) {
      const current = likes[item.id] || { liked: false, count: 0 };
      const willBeLiked = !current.liked;

      setShowHeartAnimation(prev => ({ ...prev, [itemId]: true }));
      setTimeout(() => {
        setShowHeartAnimation(prev => ({ ...prev, [itemId]: false }));
      }, 1000);

      showLikeNotificationMessage(willBeLiked, item.title);

      setLikes((prev) => ({
        ...prev,
        [item.id]: { liked: willBeLiked, count: Math.max(0, (current.count || 0) + (current.liked ? -1 : 1)) }
      }));

      const result = await toggleLike(item.id, current.liked ? 'unlike' : 'like');
      setLikes((prev) => ({
        ...prev,
        [item.id]: { liked: result.liked ?? willBeLiked, count: result.count ?? Math.max(0, (current.count || 0) + (current.liked ? -1 : 1)) }
      }));

      const finalLiked = result.liked ?? willBeLiked;
      try {
        if (finalLiked) {
          localStorage.setItem(`liked:${item.id}`, '1');
        } else {
          localStorage.removeItem(`liked:${item.id}`);
        }
      } catch { }

      setClickCount(prev => ({ ...prev, [itemId]: 0 }));
    }
  }, [clickCount, likes, showLikeNotificationMessage, trackProjectInteraction]);

  useEffect(() => {
    const timeouts = clickTimeoutRef.current;
    return () => {
      Object.values(timeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
      if (momentumFrameRef.current) {
        cancelAnimationFrame(momentumFrameRef.current);
      }
    };
  }, []);

  /**
   * Initializes likes state from localStorage cache and backend API batch request
   */
  useEffect(() => {
    try {
      const prefill = {};
      PORTFOLIO_ITEMS.forEach(item => {
        const stored = localStorage.getItem(`liked:${item.id}`);
        if (stored === '1' || stored === 'true') {
          prefill[item.id] = { liked: true, count: 0 };
        }
      });
      if (Object.keys(prefill).length) {
        setLikes(prev => ({ ...prefill, ...prev }));
      }
    } catch { }

    const initializeLikes = async () => {
      try {
        const projectIds = PORTFOLIO_ITEMS.map(item => item.id);
        const counts = await getBatchLikeCounts(projectIds);
        const statusPromises = projectIds.map(async (projectId) => {
          try {
            const status = await getLikeStatus(projectId);
            return { projectId, status };
          } catch {
            return { projectId, status: { liked: false } };
          }
        });

        const statusResults = await Promise.all(statusPromises);
        const likesData = {};
        statusResults.forEach(({ projectId, status }) => {
          likesData[projectId] = {
            liked: !!status?.liked,
            count: counts[projectId] || 0
          };
        });

        setLikes(likesData);
        logDebug('Portfolio likes initialized:', likesData);
      } catch (error) {
        logDebug('Failed to initialize likes:', error.message);
        const defaultLikes = {};
        PORTFOLIO_ITEMS.forEach(item => {
          defaultLikes[item.id] = { liked: false, count: 0 };
        });
        setLikes(defaultLikes);
      }
    };

    initializeLikes();
  }, []);

  /**
   * ============================================================================
   * 3D PERSPECTIVE COVERFLOW & AUTO-SCROLL LOOP
   * ============================================================================
   * Continuous requestAnimationFrame loop updating scroll position and recalculating
   * 3D arc perspective properties (--offset, --abs-offset, --curve-y, zIndex) for cards.
   */
  useEffect(() => {
    let animationId;

    const autoScroll = () => {
      const { isHovered: hovering, isDragging: dragging } = stateRef.current;

      if (trackRef.current) {
        const track = trackRef.current;

        // Auto-scroll when user is not hovering or actively dragging
        if (!hovering && !dragging) {
          const scrollAmount = 0.8; // Pixels per frame for smooth continuous scroll
          const actualScrollWidth = track.scrollWidth;
          const maxScroll = actualScrollWidth / 2;

          track.scrollLeft += scrollAmount;

          if (track.scrollLeft >= maxScroll - 5) {
            track.scrollLeft = 0;
          }
        }

        // Calculate relative 3D positions for each child card
        const trackWidth = track.offsetWidth;
        const trackCenter = trackWidth / 2;
        const scrollLeft = track.scrollLeft;
        const children = track.children;

        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const childCenter = child.offsetLeft + child.offsetWidth / 2 - scrollLeft;
          const distanceFromCenter = childCenter - trackCenter;
          const normalizedDistance = distanceFromCenter / (trackWidth / 2.2);
          const clampedDistance = Math.max(-2.5, Math.min(2.5, normalizedDistance));
          const absOffset = Math.abs(clampedDistance);

          // Calculate parabolic vertical curve arc displacement
          const curveY = absOffset * absOffset * 8; // Dip slightly on sides to form 3D arc curve

          child.style.setProperty('--offset', clampedDistance.toString());
          child.style.setProperty('--abs-offset', absOffset.toString());
          child.style.setProperty('--curve-y', `${curveY}px`);
          child.style.zIndex = Math.round(100 - absOffset * 10).toString();
        }
      }

      animationId = requestAnimationFrame(autoScroll);
    };

    animationId = requestAnimationFrame(autoScroll);

    const mobileResumeInterval = setInterval(() => {
      if (window.innerWidth <= 768) {
        if (stateRef.current.isDragging) {
          setIsDragging(false);
          setHasMoved(false);
        }
        setIsHovered(false);
      }
    }, 2000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setIsDragging(false);
        setHasMoved(false);
        setIsHovered(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      clearInterval(mobileResumeInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  /**
   * Applies smooth momentum physics when user finishes dragging
   */
  const applyMomentum = useCallback(() => {
    if (!trackRef.current) return;
    const track = trackRef.current;
    let velocity = velocityRef.current;

    const momentumStep = () => {
      if (Math.abs(velocity) < 0.2 || stateRef.current.isDragging) {
        return;
      }
      track.scrollLeft -= velocity;
      velocity *= 0.92; // Friction deceleration coefficient
      velocityRef.current = velocity;

      const maxScroll = track.scrollWidth / 2;
      if (track.scrollLeft >= maxScroll) {
        track.scrollLeft = 0;
      } else if (track.scrollLeft < 0) {
        track.scrollLeft = maxScroll - 1;
      }

      momentumFrameRef.current = requestAnimationFrame(momentumStep);
    };

    if (momentumFrameRef.current) {
      cancelAnimationFrame(momentumFrameRef.current);
    }
    momentumFrameRef.current = requestAnimationFrame(momentumStep);
  }, []);

  /**
   * Mouse Drag Event Handlers (Desktop)
   */
  const handleMouseDown = (e) => {
    if (!trackRef.current) return;
    if (momentumFrameRef.current) cancelAnimationFrame(momentumFrameRef.current);
    setIsDragging(true);
    setStartX(e.pageX - trackRef.current.offsetLeft);
    setScrollLeft(trackRef.current.scrollLeft);
    lastXRef.current = e.pageX;
    velocityRef.current = 0;
    trackRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !trackRef.current) return;
    e.preventDefault();
    const track = trackRef.current;
    const currentX = e.pageX;
    const deltaX = currentX - lastXRef.current;
    velocityRef.current = deltaX * 1.2;
    lastXRef.current = currentX;

    const x = currentX - track.offsetLeft;
    const walk = (x - startX) * 1.8;
    const newScrollLeft = scrollLeft - walk;
    const maxScroll = track.scrollWidth / 2;

    track.scrollLeft = newScrollLeft;

    if (track.scrollLeft >= maxScroll) {
      track.scrollLeft = 0;
      setScrollLeft(0);
      setStartX(x);
    } else if (track.scrollLeft < 0) {
      track.scrollLeft = maxScroll - 1;
      setScrollLeft(maxScroll - 1);
      setStartX(x);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      if (trackRef.current) trackRef.current.style.cursor = 'grab';
      applyMomentum();
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (trackRef.current) trackRef.current.style.cursor = 'grab';
      applyMomentum();
    }
    setIsHovered(false);
  };

  /**
   * Touch Event Handlers (Mobile & Tablet)
   */
  const handleTouchStart = useCallback((e) => {
    if (!trackRef.current) return;
    if (momentumFrameRef.current) cancelAnimationFrame(momentumFrameRef.current);
    if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);

    const touch = e.touches[0];
    setStartX(touch.clientX);
    setScrollLeft(trackRef.current.scrollLeft);
    lastXRef.current = touch.clientX;
    velocityRef.current = 0;
    setTouchStartTime(Date.now());
    setHasMoved(false);
    setIsHovered(false);
    setIsDragging(false);

    touchTimeoutRef.current = setTimeout(() => {
      setIsDragging(false);
      setHasMoved(false);
    }, 1000);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!trackRef.current) return;
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - startX);
    const currentX = touch.clientX;

    if (deltaX > 8 && !hasMoved) {
      setIsDragging(true);
      setHasMoved(true);
      e.preventDefault();
    }

    if (isDragging || hasMoved) {
      e.preventDefault();
      const moveDelta = currentX - lastXRef.current;
      velocityRef.current = moveDelta * 1.2;
      lastXRef.current = currentX;

      const walk = (startX - touch.clientX) * 1.4;
      trackRef.current.scrollLeft = scrollLeft + walk;
    }
  }, [isDragging, startX, scrollLeft, hasMoved]);

  const handleTouchCancel = useCallback(() => {
    if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
    setIsDragging(false);
    setHasMoved(false);
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const touchEndTime = Date.now();
    const touchDuration = touchEndTime - touchStartTime;

    if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);

    setIsDragging(false);
    setIsHovered(false);

    if (hasMoved) {
      e.preventDefault();
      applyMomentum();
    } else {
      setHasMoved(false);
      if (touchDuration < 200) return;
    }
  }, [hasMoved, touchStartTime, applyMomentum]);

  /**
   * Wheel Event Handler (Trackpad & Mouse wheel smooth scroll)
   */
  const handleWheel = useCallback((e) => {
    if (!trackRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const track = trackRef.current;
    const scrollAmount = e.deltaY * 1.8;
    const maxScroll = track.scrollWidth / 2;

    track.scrollLeft += scrollAmount;

    if (track.scrollLeft >= maxScroll) {
      track.scrollLeft = 0;
    } else if (track.scrollLeft < 0) {
      track.scrollLeft = maxScroll - 1;
    }
  }, []);

  const handlePointerEnter = (e) => {
    if (e.pointerType === 'mouse') setIsHovered(true);
  };

  const handlePointerLeave = (e) => {
    if (e.pointerType === 'mouse' || e.type === 'pointerleave') setIsHovered(false);
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    carousel.addEventListener('wheel', handleWheel, { passive: false });
    return () => carousel.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    track.addEventListener('touchstart', handleTouchStart, { passive: true });
    track.addEventListener('touchmove', handleTouchMove, { passive: false });
    track.addEventListener('touchend', handleTouchEnd, { passive: false });
    track.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      track.removeEventListener('touchstart', handleTouchStart);
      track.removeEventListener('touchmove', handleTouchMove);
      track.removeEventListener('touchend', handleTouchEnd);
      track.removeEventListener('touchcancel', handleTouchCancel);
      if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel]);

  return (
    <section className="portfolio" ref={portfolioRef}>
      <h2>Top Projects<br /></h2>
      <button className="discover-button" onClick={handleDiscoverClick}>DISCOVER MORE</button>
      <div
        className={`portfolio-carousel ${isCarouselAnimating ? 'carousel-animate' : ''}`}
        ref={carouselRef}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      >
        <div
          className="carousel-track"
          ref={trackRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          {[...PORTFOLIO_ITEMS, ...PORTFOLIO_ITEMS].map((item, index) => {
            const originalIndex = index % PORTFOLIO_ITEMS.length;
            const uniqueKey = `${item.id || originalIndex}-${index >= PORTFOLIO_ITEMS.length ? 'duplicate' : 'original'}`;

            return (
              <div key={uniqueKey} className="portfolio-item" style={{ position: 'relative' }}>
                {showHeartAnimation[item.id] && (
                  <div className="instagram-heart-animation">
                    <i className="fas fa-heart"></i>
                  </div>
                )}

                {/* Top-Left Heart Like Button */}
                <button
                  className={`like-button ${likes[item.id]?.liked ? 'liked' : ''}`}
                  aria-label={likes[item.id]?.liked ? 'Unlike' : 'Like'}
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const current = likes[item.id] || { liked: false, count: 0 };
                    const willBeLiked = !current.liked;

                    showLikeNotificationMessage(willBeLiked, item.title);

                    setLikes((prev) => ({
                      ...prev,
                      [item.id]: { liked: willBeLiked, count: Math.max(0, (current.count || 0) + (current.liked ? -1 : 1)) }
                    }));

                    const result = await toggleLike(item.id, current.liked ? 'unlike' : 'like');
                    setLikes((prev) => ({
                      ...prev,
                      [item.id]: { liked: result.liked ?? willBeLiked, count: result.count ?? Math.max(0, (current.count || 0) + (current.liked ? -1 : 1)) }
                    }));

                    const finalLiked = result.liked ?? willBeLiked;
                    try {
                      if (finalLiked) {
                        localStorage.setItem(`liked:${item.id}`, '1');
                      } else {
                        localStorage.removeItem(`liked:${item.id}`);
                      }
                    } catch { }
                  }}
                >
                  <span className="heart" />
                </button>

                {/* Top-Right Yellow "AVAILABLE" Ribbon */}
                {item.forSale && (
                  <div className="for-sale-ribbon">
                    <span>Available</span>
                  </div>
                )}

                {/* Bottom-Right Pink "TAP TO LIKE" Ribbon */}
                <div className="tap-to-like-indicator">
                  <i className="fas fa-hand-pointer"></i>
                  <span>Tap to like</span>
                </div>

                {/* Main Card Content Container */}
                <div
                  className="portfolio-item-content"
                  onClick={(e) => handleItemClick(e, item, originalIndex)}
                  onMouseEnter={() => handleItemHover(item, originalIndex)}
                  style={{
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img src={item.image} alt={item.alt} />
                  <p>{item.title}</p>
                </div>
              </div>
            );
          })}

          {/* Modal popup for projects coming soon */}
          {showNotification && createPortal(
            <div className="notification-overlay" onClick={() => setShowNotification(false)}>
              <div className="notification-message" onClick={(e) => e.stopPropagation()}>
                <button className="notification-close-icon" onClick={() => setShowNotification(false)} aria-label="Close notification">
                  <i className="fas fa-times"></i>
                </button>
                <div className="notification-icon">
                  <i className="fas fa-rocket"></i>
                </div>
                <h3>Coming Soon!</h3>
                <p>This exciting project is currently under development. We're crafting something extraordinary that combines innovation with exceptional user experience. Stay tuned for the unveiling!</p>
                <button className="notification-close" onClick={() => setShowNotification(false)}>
                  Got it!
                </button>
              </div>
            </div>,
            document.body
          )}

          {/* Like feedback notification toast */}
          {showLikeNotification && createPortal(
            <div className="like-notification-toast">
              <div className="like-notification-content">
                <div className="like-notification-icon">
                  <i className="fas fa-heart"></i>
                </div>
                <div className="like-notification-text">
                  <span className="like-notification-title">Liked!</span>
                  <p className="like-notification-message">{likeNotificationMessage}</p>
                </div>
                <button
                  className="like-notification-close"
                  onClick={() => setShowLikeNotification(false)}
                  aria-label="Close notification"
                >
                  <i className="fas fa-times"></i>
                </button>
                <div className="like-notification-progress"></div>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
    </section>
  );
});

PortfolioInner.displayName = 'PortfolioInner';

/**
 * Exported Portfolio component wrapped with Error Boundary for fallback error handling
 */
const Portfolio = () => (
  <CarouselErrorBoundary>
    <PortfolioInner />
  </CarouselErrorBoundary>
);

Portfolio.displayName = 'Portfolio';

export default Portfolio;

